import { useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { BookOpen, Search, Wand2 } from 'lucide-react';
import { buildKqlSuggestions, tablesForSource, type HuntingSource, type KqlSuggestion, type KqlTableDef } from '../../lib/kqlCatalog';

interface KqlIntellisenseEditorProps {
  value: string;
  onChange: (nextValue: string) => void;
  source: HuntingSource;
  customerName?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  schemaTables?: KqlTableDef[];
  schemaStatus?: string;
}

function currentToken(value: string, position: number): { token: string; start: number; end: number } {
  const before = value.slice(0, position);
  const match = before.match(/[A-Za-z0-9_.$-]+$/);
  const token = match?.[0] ?? '';
  return { token, start: position - token.length, end: position };
}

function queryMentionedTables(value: string, source: HuntingSource, schemaTables: KqlTableDef[] = []): string[] {
  const tables = tablesForSource(source, schemaTables);
  const lower = value.toLowerCase();
  return tables.filter((table) => lower.includes(table.name.toLowerCase())).map((table) => table.name).slice(0, 10);
}

function suggestionPreview(item: KqlSuggestion): string | undefined {
  if (item.example) return item.example;
  if (item.signature) return item.signature;
  return undefined;
}

export function KqlIntellisenseEditor({
  value,
  onChange,
  source,
  customerName,
  className,
  disabled,
  placeholder,
  onKeyDown,
  schemaTables = [],
  schemaStatus,
}: KqlIntellisenseEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showDoc, setShowDoc] = useState(false);

  const tokenInfo = useMemo(() => currentToken(value, cursor), [value, cursor]);
  const suggestions = useMemo(
    () => buildKqlSuggestions({ value, source, token: tokenInfo.token, cursor, runtimeTables: schemaTables }),
    [value, source, tokenInfo.token, cursor, schemaTables],
  );
  const mentionedTables = useMemo(() => queryMentionedTables(value, source, schemaTables), [value, source, schemaTables]);
  const totalTables = useMemo(() => tablesForSource(source, schemaTables).length, [source, schemaTables]);
  const activeSuggestion = suggestions[activeIndex] ?? suggestions[0];

  function syncCursor(element: HTMLTextAreaElement) {
    setCursor(element.selectionStart ?? value.length);
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onChange(event.target.value);
    syncCursor(event.target);
    setOpen(true);
    setShowDoc(false);
    setActiveIndex(0);
  }

  function insertSuggestion(item: KqlSuggestion) {
    const textarea = textareaRef.current;
    const position = textarea?.selectionStart ?? cursor;
    const token = currentToken(value, position);
    const start = item.kind === 'Snippet' ? position : token.start;
    const end = item.kind === 'Snippet' ? position : token.end;
    const prefix = item.kind === 'Snippet' && value.slice(0, start).trim() ? '\n' : '';
    const insertText = prefix + item.insertText;
    const nextValue = `${value.slice(0, start)}${insertText}${value.slice(end)}`;
    const nextPosition = start + insertText.length;
    onChange(nextValue);
    setOpen(false);
    setShowDoc(false);
    setActiveIndex(0);
    window.setTimeout(() => {
      textarea?.focus();
      if (textarea) {
        textarea.selectionStart = nextPosition;
        textarea.selectionEnd = nextPosition;
      }
      setCursor(nextPosition);
    }, 0);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.ctrlKey || event.metaKey) && event.key === ' ') {
      event.preventDefault();
      setOpen(true);
      setShowDoc(false);
      setActiveIndex(0);
      return;
    }

    if (open && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % suggestions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === 'ArrowRight' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        setShowDoc((v) => !v);
        return;
      }
      if ((event.key === 'Tab' || event.key === 'Enter') && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        insertSuggestion(suggestions[activeIndex] ?? suggestions[0]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        setShowDoc(false);
        return;
      }
    }

    onKeyDown?.(event);
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        className={className}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(event) => syncCursor(event.currentTarget)}
        onKeyUp={(event) => syncCursor(event.currentTarget)}
        onFocus={(event) => {
          syncCursor(event.currentTarget);
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => { setOpen(false); setShowDoc(false); }, 180)}
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder}
        aria-label="KQL query editor with IntelliSense"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/30 px-2 py-1"><Wand2 size={12} /> IntelliSense</span>
        <span>Ctrl/⌘ + Space for suggestions</span>
        <span>Tab or Enter to accept</span>
        <span>Ctrl/⌘ + → docs preview</span>
        <span>{totalTables} tables indexed</span>
        {schemaStatus ? <span>{schemaStatus}</span> : null}
        {customerName ? <span>Context: {customerName}</span> : null}
        {mentionedTables.length ? <span className="truncate">Detected tables: {mentionedTables.join(', ')}</span> : null}
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%-1.75rem)] z-30 grid max-h-[26rem] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 overflow-y-auto p-2">
            <div className="mb-1 flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Search size={12} /> Context-aware KQL suggestions
            </div>
            <div className="space-y-1">
              {suggestions.map((item, index) => (
                <button
                  key={`${item.kind}-${item.label}-${index}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertSuggestion(item);
                  }}
                  className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition ${index === activeIndex ? 'bg-primary/10 text-foreground' : 'hover:bg-muted'}`}
                >
                  <span className="mt-0.5 min-w-[4.8rem] rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-center text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{item.kind}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-semibold text-foreground">{item.label}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">{item.detail}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className={`hidden border-l border-border bg-muted/20 p-3 text-xs lg:block ${showDoc || activeSuggestion ? '' : 'opacity-70'}`}>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground"><BookOpen size={12} /> Inline docs</div>
            {activeSuggestion ? (
              <div className="space-y-3">
                <div>
                  <div className="font-semibold text-foreground">{activeSuggestion.label}</div>
                  <div className="mt-1 text-muted-foreground">{activeSuggestion.detail}</div>
                </div>
                {activeSuggestion.signature ? (
                  <pre className="whitespace-pre-wrap rounded-lg border border-border bg-background p-2 font-mono text-[11px] text-foreground">{activeSuggestion.signature}</pre>
                ) : null}
                {suggestionPreview(activeSuggestion) ? (
                  <div>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Example</div>
                    <pre className="whitespace-pre-wrap rounded-lg border border-border bg-background p-2 font-mono text-[11px] text-foreground">{suggestionPreview(activeSuggestion)}</pre>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="text-muted-foreground">Hover or arrow through suggestions to see syntax, argument hints and examples.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
